import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Link2, Plus, X, Search, Loader2, User, Briefcase, UserPlus, Ticket,
  Mic, Receipt, Laptop, FolderKanban,
} from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import ConfirmDialog from '../ConfirmDialog';
import NewProjectModal from '../NewProjectModal';

// ----- Configuracion de tipos -----
//
// Cada tipo define como buscar, como renderizar el resultado y como construir la URL
// para abrir la entidad. Los endpoints existen ya en el backend.

const ENTITY_TYPES = {
  empleado: {
    label: 'Empleado',
    icon: User,
    color: '#06b6d4',
    bg: 'bg-cyan-50',
    text: 'text-cyan-700',
    fetch: async () => {
      const { data } = await axiosInstance.get('/api/empleados');
      return (data || []).map((e) => ({
        id: e.id,
        label: e.nombre || e.nombre_completo,
        sub: e.cargo || e.area_nombre || e.ficha || '',
      }));
    },
    routeFor: (id) => `/empleados?id=${id}`,
  },
  vacante: {
    label: 'Vacante',
    icon: Briefcase,
    color: '#f97316',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    fetch: async () => {
      const { data } = await axiosInstance.get('/api/vacantes');
      return (data || []).map((v) => ({
        id: v.id,
        label: v.cargo_nombre || v.titulo || `Vacante #${v.id}`,
        sub: v.departamento_nombre || v.area_nombre || '',
      }));
    },
    routeFor: () => `/vacantes`,
  },
  candidato: {
    label: 'Candidato',
    icon: UserPlus,
    color: '#8b5cf6',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    fetch: async () => {
      const { data } = await axiosInstance.get('/api/candidatos');
      return (data || []).map((c) => ({
        id: c.id,
        label: c.nombre_completo || c.nombre || `Candidato #${c.id}`,
        sub: c.vacante_titulo || c.estatus || '',
      }));
    },
    routeFor: () => `/vacantes`,
  },
  ticket: {
    label: 'Ticket',
    icon: Ticket,
    color: '#3b82f6',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    fetch: async () => {
      const { data } = await axiosInstance.get('/api/tickets');
      return (data || []).map((t) => ({
        id: t.id,
        label: t.titulo || `Ticket #${t.id}`,
        sub: t.categoria_label || t.estado || '',
      }));
    },
    routeFor: (id) => `/soporte-ti?ticket=${id}`,
  },
  minuta: {
    label: 'Minuta',
    icon: Mic,
    color: '#10b981',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    fetch: async () => {
      const { data } = await axiosInstance.get('/api/minutas');
      return (data || []).map((m) => ({
        id: m.id,
        label: m.titulo || `Minuta #${m.id}`,
        sub: m.fecha ? new Date(m.fecha).toLocaleDateString('es-VE', { day: 'numeric', month: 'short' }) : '',
      }));
    },
    routeFor: (id) => `/grabacion-reuniones?minuta=${id}`,
  },
  viatico: {
    label: 'Viático',
    icon: Receipt,
    color: '#f59e0b',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    fetch: async () => {
      const { data } = await axiosInstance.get('/api/viaticos');
      const lista = data?.mis_solicitudes || data || [];
      return lista.map((v) => ({
        id: v.id,
        label: `${v.codigo || `Viático #${v.id}`}`,
        sub: v.destino || v.motivo || '',
      }));
    },
    routeFor: () => `/solicitud-viaticos`,
  },
  asset: {
    label: 'Equipo',
    icon: Laptop,
    color: '#64748b',
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    fetch: async () => {
      const { data } = await axiosInstance.get('/api/activos');
      return (data || []).map((a) => ({
        id: a.id,
        label: `${a.activo_fijo || a.modelo || `Equipo #${a.id}`}`,
        sub: a.tipo || '',
      }));
    },
    routeFor: () => `/equipos`,
  },
  proyecto: {
    label: 'Proyecto',
    icon: FolderKanban,
    color: '#0e7490',
    bg: 'bg-cyan-50',
    text: 'text-cyan-800',
    // Solo los proyectos donde el usuario es miembro (los que puede abrir).
    fetch: async () => {
      const { data } = await axiosInstance.get('/api/proyectos');
      return (data || []).map((p) => ({
        id: p.id,
        label: p.nombre,
        sub: p.slug,
        slug: p.slug,
      }));
    },
    // La URL usa el slug (editable), asi que se resuelve con el detalle cargado.
    routeFor: (id, detail) => (detail?.slug ? `/proyectos/${detail.slug}` : '/proyectos'),
  },
};

const TYPE_LIST = Object.keys(ENTITY_TYPES);

// ----- Item de relacion existente -----

const RelacionItem = ({ rel, onUnlink }) => {
  const navigate = useNavigate();
  const cfg = ENTITY_TYPES[rel.entidad_tipo];
  const [detail, setDetail] = useState(null);

  // Cargar label real (best-effort, sin bloquear UI)
  useEffect(() => {
    if (!cfg) return;
    let alive = true;
    (async () => {
      try {
        const list = await cfg.fetch();
        const match = list.find((x) => x.id === rel.entidad_id);
        if (alive && match) setDetail(match);
      } catch {}
    })();
    return () => { alive = false; };
  }, [rel.entidad_id, rel.entidad_tipo, cfg]);

  if (!cfg) return null;
  const Icon = cfg.icon;
  const url = cfg.routeFor(rel.entidad_id, detail);

  return (
    <motion.li
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.15 }}
      className="group flex items-center gap-2 px-2 py-1.5 -mx-2 rounded-md hover:bg-slate-50 transition-colors"
    >
      <span className={`inline-flex items-center justify-center w-5 h-5 rounded ${cfg.bg} ${cfg.text} flex-shrink-0`}>
        <Icon size={11} strokeWidth={2} />
      </span>
      <a
        href={url}
        onClick={(e) => {
          // Ctrl/Cmd+click queda como navegacion nativa (nueva pestania). Click normal
          // usa React Router para no recargar todo el SPA.
          if (e.metaKey || e.ctrlKey) return;
          e.preventDefault();
          navigate(url);
        }}
        className="flex-1 min-w-0 flex items-baseline gap-1.5"
      >
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
          {cfg.label}
        </span>
        <span className="text-xs text-slate-800 truncate flex-1 hover:text-cyan-700 transition-colors">
          {detail?.label || `#${rel.entidad_id}`}
        </span>
      </a>
      <button
        onClick={() => onUnlink(rel)}
        className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-rose-600 transition-opacity"
        title="Quitar vínculo"
      >
        <X size={11} />
      </button>
    </motion.li>
  );
};

// ----- Selector (popover) -----

const EntityPicker = ({ existentes = [], onPick, onClose, onCrearProyecto }) => {
  const [tipo, setTipo] = useState('empleado');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cargar al cambiar de tipo
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setItems([]);
    ENTITY_TYPES[tipo].fetch()
      .then((list) => { if (alive) setItems(list || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tipo]);

  const existSet = useMemo(() => {
    const s = new Set();
    existentes.forEach((r) => s.add(`${r.entidad_tipo}-${r.entidad_id}`));
    return s;
  }, [existentes]);

  const filtrados = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = items;
    if (q) {
      list = list.filter(
        (i) => (i.label || '').toLowerCase().includes(q) || (i.sub || '').toLowerCase().includes(q)
      );
    }
    return list.slice(0, 30);
  }, [items, query]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -4, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -4, scale: 0.97 }}
      transition={{ duration: 0.12 }}
      className="absolute right-0 top-full mt-1.5 w-80 bg-white rounded-xl shadow-2xl ring-1 ring-slate-200 overflow-hidden z-30"
    >
      {/* Tabs de tipo */}
      <div className="flex items-center gap-0.5 px-1.5 pt-1.5 overflow-x-auto no-scrollbar border-b border-slate-100">
        {TYPE_LIST.map((k) => {
          const cfg = ENTITY_TYPES[k];
          const Icon = cfg.icon;
          const isActive = tipo === k;
          return (
            <button
              key={k}
              onClick={() => setTipo(k)}
              title={cfg.label}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium tracking-tight whitespace-nowrap transition-colors ${
                isActive ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <Icon size={11} strokeWidth={2} />
              {cfg.label}
            </button>
          );
        })}
      </div>

      {/* Buscador */}
      <div className="relative px-2 pt-2">
        <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Buscar ${ENTITY_TYPES[tipo].label.toLowerCase()}...`}
          className="w-full pl-7 pr-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-md placeholder-slate-400 focus:outline-none focus:bg-white focus:border-slate-300"
        />
      </div>

      {tipo === 'proyecto' && onCrearProyecto && (
        <button
          type="button"
          onClick={onCrearProyecto}
          className="mx-2 mt-2 w-[calc(100%-1rem)] inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-cyan-800 bg-cyan-50 hover:bg-cyan-100 rounded-md transition-colors"
        >
          <Plus size={12} strokeWidth={2.5} />
          Crear proyecto y vincularlo a esta tarea
        </button>
      )}

      {/* Lista */}
      <div className="max-h-72 overflow-y-auto py-1 mt-1">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 size={14} className="text-slate-400 animate-spin" />
          </div>
        ) : filtrados.length === 0 ? (
          <div className="px-3 py-4 text-xs text-slate-400 text-center">
            {query ? 'Sin resultados' : `Sin ${ENTITY_TYPES[tipo].label.toLowerCase()}s disponibles`}
          </div>
        ) : (
          filtrados.map((item) => {
            const key = `${tipo}-${item.id}`;
            const yaVinculada = existSet.has(key);
            return (
              <button
                key={key}
                disabled={yaVinculada}
                onClick={() => { onPick(tipo, item); }}
                className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors ${
                  yaVinculada ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50'
                }`}
              >
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-slate-800 truncate">{item.label}</span>
                  {item.sub && (
                    <span className="block text-[11px] text-slate-400 truncate">{item.sub}</span>
                  )}
                </span>
                {yaVinculada && (
                  <span className="text-[10px] text-slate-400 font-medium">Vinculado</span>
                )}
              </button>
            );
          })
        )}
      </div>
    </motion.div>
  );
};

// ----- Componente principal -----

const RelatedEntities = ({ tareaId, tareaTitulo = '', relaciones = [], onChanged }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [crearProyectoOpen, setCrearProyectoOpen] = useState(false);
  const [relToUnlink, setRelToUnlink] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pickerOpen]);

  const handlePick = useCallback(async (tipo, item) => {
    setPickerOpen(false);
    try {
      await axiosInstance.post(`/api/tareas/${tareaId}/relaciones`, {
        entidad_tipo: tipo,
        entidad_id: item.id,
      });
      onChanged?.();
    } catch (err) {
      console.error('Error vinculando:', err);
    }
  }, [tareaId, onChanged]);

  // Proyecto hijo: se crea (con o sin plantilla) y queda vinculado a la tarea
  // de origen. El usuario sigue en la tarea; el vinculo lleva al proyecto.
  const handleProyectoCreado = useCallback(async (proyecto) => {
    setCrearProyectoOpen(false);
    if (!proyecto?.id) return;
    try {
      await axiosInstance.post(`/api/tareas/${tareaId}/relaciones`, {
        entidad_tipo: 'proyecto',
        entidad_id: proyecto.id,
      });
      onChanged?.();
    } catch (err) {
      console.error('Error vinculando proyecto:', err);
    }
  }, [tareaId, onChanged]);

  const doUnlink = useCallback(async () => {
    if (!relToUnlink) return;
    const rel = relToUnlink;
    setRelToUnlink(null);
    try {
      await axiosInstance.delete(`/api/tareas/${tareaId}/relaciones/${rel.id}`);
      onChanged?.();
    } catch (err) {
      console.error('Error desvinculando:', err);
    }
  }, [tareaId, onChanged, relToUnlink]);

  return (
    <section>
      <div className="flex items-center gap-1.5 mb-2">
        <Link2 size={11} strokeWidth={2} className="text-slate-400" />
        <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
          Relacionado con
        </span>
        {relaciones.length > 0 && (
          <span className="text-[10px] text-slate-400 tabular-nums">{relaciones.length}</span>
        )}
        <div className="flex-1 h-px bg-slate-200/60" />
        <div className="relative" ref={containerRef}>
          <button
            onClick={() => setPickerOpen((v) => !v)}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded transition-colors ${
              pickerOpen
                ? 'bg-slate-100 text-slate-700'
                : 'text-slate-400 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Plus size={10} strokeWidth={2.5} />
            Vincular
          </button>
          <AnimatePresence>
            {pickerOpen && (
              <EntityPicker
                existentes={relaciones}
                onPick={handlePick}
                onClose={() => setPickerOpen(false)}
                onCrearProyecto={() => { setPickerOpen(false); setCrearProyectoOpen(true); }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {relaciones.length === 0 ? (
        <p className="text-xs text-slate-400 italic px-1 py-1">
          Aún sin vínculos a otras entidades.
        </p>
      ) : (
        <ul>
          <AnimatePresence initial={false}>
            {relaciones.map((rel) => (
              <RelacionItem key={rel.id} rel={rel} onUnlink={setRelToUnlink} />
            ))}
          </AnimatePresence>
        </ul>
      )}

      <NewProjectModal
        isOpen={crearProyectoOpen}
        onClose={() => setCrearProyectoOpen(false)}
        onCreated={handleProyectoCreado}
        nombreInicial={tareaTitulo}
        subtitulo="Quedará vinculado a esta tarea en Relacionado con."
      />

      <ConfirmDialog
        isOpen={!!relToUnlink}
        title="¿Quitar este vínculo?"
        message={relToUnlink ? `La tarea dejará de aparecer como vinculada a ${ENTITY_TYPES[relToUnlink.entidad_tipo]?.label.toLowerCase() || 'esta entidad'}. Puedes volver a vincularla cuando quieras.` : ''}
        variant="warning"
        confirmLabel="Quitar"
        onConfirm={doUnlink}
        onClose={() => setRelToUnlink(null)}
      />
    </section>
  );
};

export default RelatedEntities;
